import { initializeApp } from "firebase/app";
import {
  getAuth,
  GoogleAuthProvider,
  signInWithPopup,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  getFirestore,
  doc,
  setDoc,
  getDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  serverTimestamp,
  Timestamp,
  increment,
} from "firebase/firestore";
import { getFunctions, httpsCallable } from "firebase/functions";
import { toast } from "@/components/ui/use-toast";

// Call same-origin API; Hosting rewrites /api/* → Cloud Run
// This allows us to route API calls through Firebase Hosting to our Cloud Functions
const API_BASE = "/api";

// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDFtOM2V6C97p3nq9ihyq44zQwQZGer8cs",
  authDomain: "pokescan2.firebaseapp.com",
  databaseURL:
    "https://pokescan2-default-rtdb.europe-west1.firebasedatabase.app",
  projectId: "pokescan2",
  storageBucket: "pokescan2.applestorage.app",
  messagingSenderId: "37219861312",
  appId: "1:37219861312:web:8e9aa978ac833b5ea070a5",
  measurementId: "G-Z2MYRF6JDZ",
};

// Initialize Firebase
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const functions = getFunctions(app);
export const googleProvider = new GoogleAuthProvider();

// Listen to auth state changes (optional)
export const onAuthChange = (callback) => onAuthStateChanged(auth, callback);

// Sign in with Google (creates new Firestore user if needed)
export const signInWithGoogle = async () => {
  try {
    const result = await signInWithPopup(auth, googleProvider);
    const user = result.user;
    const userRef = doc(db, "users", user.uid);
    const userSnap = await getDoc(userRef);

    if (!userSnap.exists()) {
      await setDoc(userRef, {
        email: user.email,
        displayName: user.displayName,
        photoURL: user.photoURL,
        createdAt: Timestamp.now(),
        remainingScans: 2,
        subscribed: false,
        subscriptionTier: null,
        subscriptionEndDate: null,
      });
      toast({
        title: "Welcome to PokeVision!",
        description: "Your account has been created. You have 2 free scans.",
      });
    } else {
      toast({
        title: "Welcome back!",
        description: "You've successfully signed in.",
      });
    }
    return user;
  } catch (error) {
    console.error("Error signing in with Google:", error);
    toast({
      title: "Sign In Error",
      description: error.message,
      variant: "destructive",
    });
    return null;
  }
};

// Register with email and password
export const registerWithEmail = async (email, password, displayName) => {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    const user = result.user;
    const userRef = doc(db, "users", user.uid);
    await setDoc(userRef, {
      email,
      displayName,
      createdAt: Timestamp.now(),
      remainingScans: 2,
      subscribed: false,
      subscriptionTier: null,
      subscriptionEndDate: null,
    });
    toast({
      title: "Welcome to PokeVision!",
      description: "Your account has been created. You have 2 free scans.",
    });
    return user;
  } catch (error) {
    console.error("Error registering:", error);
    toast({
      title: "Registration Error",
      description: error.message,
      variant: "destructive",
    });
    return null;
  }
};

// Login with email and password
export const loginWithEmail = async (email, password) => {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    toast({
      title: "Welcome back!",
      description: "You've successfully signed in.",
    });
    return result.user;
  } catch (error) {
    console.error("Error logging in:", error);
    toast({
      title: "Login Error",
      description: error.message,
      variant: "destructive",
    });
    return null;
  }
};

// Logout user
export const logoutUser = async () => {
  try {
    await signOut(auth);
    toast({
      title: "Logged out",
      description: "You've been successfully logged out.",
    });
    return true;
  } catch (error) {
    console.error("Error logging out:", error);
    toast({
      title: "Logout Error",
      description: error.message,
      variant: "destructive",
    });
    return false;
  }
};

// Get or create user profile document (for synchronizing additional data)
export const createUserProfileDocument = async (user, additionalData = {}) => {
  if (!user) return null;
  const userRef = doc(db, "users", user.uid);
  const snap = await getDoc(userRef);
  if (!snap.exists()) {
    const { displayName, email, photoURL } = user;
    try {
      await setDoc(userRef, {
        displayName,
        email,
        photoURL,
        createdAt: serverTimestamp(),
        remainingScans: 2,
        subscribed: false,
        subscriptionTier: null,
        subscriptionEndDate: null,
        ...additionalData,
      });
    } catch (error) {
      console.error("Error creating user document", error);
      toast({
        title: "Error",
        description: "Failed to create user profile. Please try again.",
        variant: "destructive",
      });
    }
  }
  return getUserDocument(user.uid);
};

// Fetch user document by uid
export const getUserDocument = async (uid) => {
  if (!uid) return null;
  try {
    const userRef = doc(db, "users", uid);
    const snap = await getDoc(userRef);
    return snap.exists() ? { id: snap.id, ...snap.data() } : null;
  } catch (error) {
    console.error("Error fetching user document", error);
    toast({
      title: "Error",
      description: "Failed to retrieve user profile. Please try again.",
      variant: "destructive",
    });
    return null;
  }
};

// Get user subscription data
export const getUserSubscriptionData = async (userId) => {
  try {
    const userRef = doc(db, "users", userId);
    const snap = await getDoc(userRef);
    if (!snap.exists()) return { subscribed: false, remainingScans: 0, subscriptionTier: null, subscriptionEndDate: null, subscriptionId: null };
    const data = snap.data();
    
    // Handle subscriptionEndDate that might not be a Firestore Timestamp
    let subscriptionEndDate = null;
    if (data.subscriptionEndDate) {
      if (typeof data.subscriptionEndDate.toDate === 'function') {
        subscriptionEndDate = data.subscriptionEndDate.toDate();
      } else if (data.subscriptionEndDate.seconds) {
        // If it's in Firestore Timestamp format but not a proper Timestamp object
        subscriptionEndDate = new Date(data.subscriptionEndDate.seconds * 1000);
      } else if (typeof data.subscriptionEndDate === 'number') {
        // If it's a Unix timestamp in seconds
        subscriptionEndDate = new Date(data.subscriptionEndDate * 1000);
      }
    }

    const isSubscribed = data.subscribed && subscriptionEndDate && subscriptionEndDate > new Date();
    return {
      subscribed: isSubscribed,
      remainingScans: data.remainingScans || 0,
      subscriptionTier: data.subscriptionTier || "basic",
      subscriptionEndDate,
      subscriptionId: data.subscriptionId || null,
    };
  } catch (error) {
    console.error("Error getting user subscription data:", error);
    return { subscribed: false, remainingScans: 0, subscriptionTier: null, subscriptionEndDate: null, subscriptionId: null };
  }
};

// Cancel user subscription
export const cancelUserSubscription = async (): Promise<boolean> => {
  try {
    const user = auth.currentUser;
    if (!user) {
      toast({
        title: "Error",
        description: "You must be logged in to cancel a subscription",
        variant: "destructive"
      });
      return false;
    }

    // First, get the user's subscription data to get the subscriptionId
    const userDoc = await getDoc(doc(db, "users", user.uid));
    if (!userDoc.exists()) {
      toast({
        title: "Error", 
        description: "User data not found",
        variant: "destructive"
      });
      return false;
    }

    const userData = userDoc.data();
    const subscriptionId = userData.subscriptionId;
    
    if (!subscriptionId) {
      toast({
        title: "Error",
        description: "No active subscription found",
        variant: "destructive"
      });
      return false;
    }

    console.log(`Attempting to cancel subscription: ${subscriptionId}`);
    
    // Make API call to cancel subscription
    const idToken = await user.getIdToken();
    const CANCEL_URL = "https://cancelsubscription-f2cz2uwarq-uc.a.run.app";

    const response = await fetch(CANCEL_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`
      },
      body: JSON.stringify({ subscriptionId })
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error("Error response:", errorText);
      throw new Error(`Failed to cancel subscription. Please contact us at +47 481 05 946: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    console.log("Cancellation response:", data);
    
    return data.success === true;
  } catch (error) {
    console.error("cancelUserSubscription error:", error);
    toast({
      title: "Subscription Cancellation Failed",
      description: error instanceof Error ? error.message : "An unknown error occurred",
      variant: "destructive"
    });
    return false;
  }
};

// Firestore card collection management
export const addCardToCollection = async (cardId, cardData) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("User not authenticated");
    const collRef = collection(db, "users", currentUser.uid, "collection");
    const q = query(collRef, where("cardId", "==", cardId));
    const snap = await getDocs(q);
    if (!snap.empty) {
      toast({ title: "Card already in collection", description: "This card is already in your collection." });
      return false;
    }
    const docRef = doc(collRef);
    await setDoc(docRef, { cardId, cardData, addedAt: Timestamp.now(), isFavorite: false });
    toast({ title: "Card added", description: "Card was added to your collection." });
    return true;
  } catch (error) {
    console.error("Error adding card to collection:", error);
    toast({ title: "Error", description: error.message || "Failed to add card to collection.", variant: "destructive" });
    return false;
  }
};

export const getUserCollection = async () => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) return [];
    const collRef = collection(db, "users", currentUser.uid, "collection");
    const snap = await getDocs(collRef);
    return snap.docs.map(d => ({ id: d.id, ...d.data() }));
  } catch (error) {
    console.error("Error fetching user collection:", error);
    toast({ title: "Error", description: "Failed to load your collection. Please try again later.", variant: "destructive" });
    return [];
  }
};

export const toggleFavorite = async (docId) => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) throw new Error("User not authenticated");
    const cardRef = doc(db, "users", currentUser.uid, "collection", docId);
    const snap = await getDoc(cardRef);
    if (!snap.exists()) throw new Error("Card not found");
    const { isFavorite = false } = snap.data();
    await updateDoc(cardRef, { isFavorite: !isFavorite, updatedAt: serverTimestamp() });
    return true;
  } catch (error) {
    console.error("Error toggling favorite:", error);
    toast({ title: "Error", description: "Failed to update favorite status.", variant: "destructive" });
    return false;
  }
};

// Example Cloud Function calls (Stripe, scan quota, etc.)
export const getStripePayments = async () => {
  try {
    const fn = httpsCallable(functions, 'ext-firestore-stripe-payments-getStripePayments');
    const res = await fn();
    return res.data;
  } catch (error) {
    console.error(error);
    toast({ title: "Error", description: error.message || "Failed to retrieve Stripe payments.", variant: "destructive" });
    return [];
  }
};

export const createCheckoutSession = async (priceId, origin) => {
  try {
    const fn = httpsCallable(functions, 'createCheckoutSession');
    const res = await fn({ priceId, origin });
    return res.data;
  } catch (error) {
    console.error(error);
    toast({ title: "Error", description: error.message || "Failed to create checkout session.", variant: "destructive" });
    return null;
  }
};

export const getScanQuota = async () => {
  try {
    const fn = httpsCallable(functions, 'getScanQuota');
    const res = await fn();
    return res.data;
  } catch (error) {
    console.error(error);
    toast({ title: "Error", description: error.message || "Failed to retrieve scan quota.", variant: "destructive" });
    return null;
  }
};

export const decrementScanCount = async () => {
  try {
    const fn = httpsCallable(functions, 'decrementScanCount');
    const res = await fn();
    return res.data;
  } catch (error) {
    console.error(error);
    toast({ title: "Error", description: error.message || "Failed to decrement scan count.", variant: "destructive" });
    return null;
  }
};

// Update user subscription status - FIXED to correctly handle PRO tier
export const updateUserSubscriptionStatus = async (
  userId: string, 
  tier: 'basic' | 'pro'
): Promise<void> => {
  try {
    console.log(`Updating user ${userId} subscription status to ${tier}`);
    
    // Set appropriate scan count for each tier
    const scansForTier = tier === 'pro' ? 800 : 30;
    
    await updateDoc(doc(db, "users", userId), {
      subscribed: true,
      subscriptionTier: tier,
      remainingScans: scansForTier,
      maxScans: scansForTier,
      updatedAt: serverTimestamp()
    });
    
    console.log(`Successfully updated subscription: ${tier} with ${scansForTier} scans`);
  } catch (error) {
    console.error("Error updating subscription status:", error);
    throw error;
  }
};
